import { useCallback, useEffect, useRef, useState } from "react";

/**
 * La dictée, par la reconnaissance vocale du navigateur.
 *
 * Le patron, le 17 septembre : « pour la création de devis assisté par IA il
 * faut un mode saisie vocale pour éviter aux gars de taper leur message sur le
 * clavier ». Décrire un chantier au clavier, sur un téléphone, les doigts
 * pleins d'enduit, c'est la friction qui fait renoncer — et l'IA attend
 * justement une description parlée, pas un formulaire.
 *
 * **Le navigateur fait le travail, pas nous.** Aucune clé, aucun service à
 * payer, aucun son qui transite par nos serveurs. En contrepartie, la
 * reconnaissance n'existe pas partout : Chrome et Edge l'ont (Chrome envoie
 * l'audio aux serveurs de Google pour le transcrire), Safari depuis iOS 14.5,
 * Firefox pas du tout. `disponible` dit la vérité, et l'écran propose alors le
 * micro du clavier, qui fait la même chose.
 *
 * La reconnaissance s'arrête d'elle-même après un silence : tant que la
 * personne n'a pas touché « Arrêter », on la relance. Trois relances coup sur
 * coup sans un mot dit, on s'arrête — c'est un micro coupé, pas une pause.
 */

/** Le peu de l'API qu'on utilise : `lib.dom` ne la déclare pas partout. */
interface ResultatVocal {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; length: number; 0: { transcript: string } }>;
}

interface Reconnaissance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: ResultatVocal) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type Constructeur = new () => Reconnaissance;

function moteur(): Constructeur | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: Constructeur;
    webkitSpeechRecognition?: Constructeur;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Ce qu'on dit à quelqu'un qui tient un téléphone, pas un code d'erreur. */
const MESSAGES: Record<string, string> = {
  "not-allowed":
    "Le micro est refusé. Autorisez-le pour ce site dans les réglages du navigateur.",
  "service-not-allowed":
    "Le micro est refusé. Autorisez-le pour ce site dans les réglages du navigateur.",
  "audio-capture": "Aucun micro trouvé sur cet appareil.",
  network: "La dictée a besoin d’internet pour transcrire.",
  "language-not-supported": "Ce navigateur ne sait pas transcrire le français.",
};

const RELANCES_A_VIDE = 3;

export function useDictation({ onTexte }: { onTexte: (phrase: string) => void }) {
  const [disponible] = useState(() => moteur() != null);
  const [ecoute, setEcoute] = useState(false);
  const [provisoire, setProvisoire] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  const instance = useRef<Reconnaissance | null>(null);
  const voulu = useRef(false);
  const aEntendu = useRef(false);
  const relancesAVide = useRef(0);
  // Le rappel change à chaque rendu — le champ de saisie qu'il remplit change
  // de valeur : la reconnaissance, elle, vit plus longtemps qu'un rendu.
  const rappel = useRef(onTexte);
  rappel.current = onTexte;

  const arreter = useCallback(() => {
    voulu.current = false;
    setEcoute(false);
    setProvisoire("");
    instance.current?.stop();
  }, []);

  const demarrer = useCallback(() => {
    const Moteur = moteur();
    if (!Moteur) return;
    setErreur(null);
    voulu.current = true;
    aEntendu.current = false;
    relancesAVide.current = 0;

    const reco = instance.current ?? new Moteur();
    instance.current = reco;
    reco.lang = "fr-FR";
    reco.continuous = true;
    reco.interimResults = true;
    reco.maxAlternatives = 1;

    reco.onresult = (e) => {
      let enCours = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const resultat = e.results[i]!;
        const texte = resultat[0].transcript;
        if (resultat.isFinal) {
          aEntendu.current = true;
          relancesAVide.current = 0;
          const phrase = texte.trim();
          if (phrase) rappel.current(phrase);
        } else {
          enCours += texte;
        }
      }
      setProvisoire(enCours.trim());
    };

    reco.onerror = (e) => {
      // « aborted » : c'est nous qui avons arrêté. « no-speech » : un silence,
      // que la relance traite déjà. Ni l'un ni l'autre n'est à montrer.
      if (e.error === "aborted" || e.error === "no-speech") return;
      setErreur(MESSAGES[e.error] ?? "La dictée s’est interrompue. Réessayez.");
      voulu.current = false;
      setEcoute(false);
      setProvisoire("");
    };

    reco.onend = () => {
      setProvisoire("");
      if (!voulu.current) {
        setEcoute(false);
        return;
      }
      // Arrêt sur silence : on reprend. Trois fois sans un mot, c'est que rien
      // n'arrive — micro coupé, casque débranché — et insister ne sert à rien.
      if (!aEntendu.current) relancesAVide.current += 1;
      aEntendu.current = false;
      if (relancesAVide.current >= RELANCES_A_VIDE) {
        voulu.current = false;
        setEcoute(false);
        setErreur("Rien n’a été entendu. Vérifiez le micro, puis réessayez.");
        return;
      }
      try {
        reco.start();
      } catch {
        // Déjà démarrée : le navigateur l'a relancée de lui-même.
      }
    };

    try {
      reco.start();
      setEcoute(true);
    } catch {
      // `start()` sur une reconnaissance déjà en cours lève : on ignore, elle
      // écoute déjà.
      setEcoute(true);
    }
  }, []);

  const basculer = useCallback(() => {
    if (ecoute) arreter();
    else demarrer();
  }, [ecoute, arreter, demarrer]);

  useEffect(
    () => () => {
      voulu.current = false;
      instance.current?.abort();
      instance.current = null;
    },
    [],
  );

  return { disponible, ecoute, provisoire, erreur, demarrer, arreter, basculer };
}

/**
 * Coller une phrase dictée à la suite de ce qui est déjà écrit.
 *
 * Une majuscule après un point, une espace entre deux phrases : la dictée rend
 * du texte nu, et ce que l'IA recevra doit se lire.
 */
export function ajouterPhrase(texte: string, phrase: string): string {
  const base = texte.trimEnd();
  const mot = phrase.trim();
  if (!mot) return base;
  // Chaque bout dicté est une phrase : on ferme la précédente d'un point s'il
  // en manque un, et celle qui arrive prend sa majuscule.
  const majuscule = mot.charAt(0).toUpperCase() + mot.slice(1);
  if (base === "") return majuscule;
  return `${base}${/[.!?…]$/.test(base) ? "" : "."} ${majuscule}`;
}
